import React, { useRef, useState } from 'react';
import moment from 'moment';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import CommonButton from 'components/CommonButton/CommonButton';
import { renderToast } from 'components/Toast/Toast';
import { IAppointmentStatus } from 'hooks/useAppointment';
import { loadAuthToken } from 'utils/storage';
import axiosInstance from 'apis/axiosInstance';
import { IAppointmentDetail } from 'interfaces/appointments';
import { APPOINTMENT_STATUS, PMS_TYPE } from 'utils/constants';
import { convertDurationForFE } from 'utils/convertDuration';
import { groupBy, isNil, orderBy } from 'lodash';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  DATE_TIME_FORMAT,
  parseDateStringWithoutAffectTz,
} from 'utils/datetime';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import { TRAFFIC_SOURCES } from 'pages/OnlineSchedulingReportPage/TrafficBySource/TRAFFIC_SOURCES';

const HEADERS = [
  { label: 'Appointment Date & Time', key: 'appointmentDate' },
  { label: 'Appointment Status', key: 'appointmentStatus' },
  { label: 'Patient First Name', key: 'patientFirstName' },
  { label: 'Patient Last Name', key: 'patientLastName' },
  { label: 'Patient Phone Number', key: 'patientPhoneNumber' },
  { label: 'Patient Email', key: 'patientEmail' },
  { label: 'Practice', key: 'clinicName' },
  { label: 'Practitioner', key: 'practitionerName' },
  { label: 'Service', key: 'serviceName' },
  { label: 'Time of Appt Request', key: 'requestTime' },
  { label: 'Date of Appt Request', key: 'requestDate' },
  { label: 'Day of Appt Request', key: 'requestDay' },
  { label: 'After Hours', key: 'isBookedAfterClinicHour' },
  { label: 'New Patient', key: 'isNewPatient' },
  { label: 'Source', key: 'source' },
];

const ALL_STATUS = 'all';

type NormalizedDataType = {
  appointmentDate: string;
  clinicId: string;
  clinicName: string;
  patientFirstName: string;
  patientLastName: string;
  practitionerName: string;
  serviceName: string;
  createdAt: string;
  errorType: string;
  status: string;
  isAccepted: boolean;
  isCancelled: boolean;
  requestTime: string;
  requestDate: string;
  requestDay: string;
  appointmentStatus: string;
  patientPhoneNumber: string;
  patientEmail: string;
  isBookedAfterClinicHour: boolean;
  isNewPatient: boolean;
  source: string;
  currentStatus?: string;
};

type SummaryDataType = {
  currentStatus: string;
  totalApptsByService: { serviceName: string | null; total: number | null }[];
  grandTotalAppts: number;
  totalNewPatients: number;
  totalAfterHoursAppts: number;
  data: { serviceName: string | null; total: number | null }[];
};

const EXTRA_WIDTH = 10;
const SPECIAL_CHARACTER = ['*', '?', ':', '\\', '/', '[', ']'];

const ExcelExport = ({
  appointmentStatus,
  clinicIds: selectedClinicIds,
  dateRange,
  sort,
  sortBy,
  isDataLoading,
}: {
  appointmentStatus: IAppointmentStatus;
  clinicIds: string[];
  dateRange: {
    fromDate: string;
    toDate: string;
  };
  sort: 'asc' | 'desc';
  sortBy: string;
  isDataLoading: boolean;
}) => {
  const defaultWidth =
    Math.max(...HEADERS.map((item) => item.label.length)) + EXTRA_WIDTH;

  const columnWidth = useRef(
    Array.from({ length: HEADERS.length }).fill(defaultWidth)
  ) as any;

  const workBook = useRef(new ExcelJS.Workbook());

  const [isLoading, setIsLoading] = useState(false);

  const normalizePractitionerName = (item: IAppointmentDetail) => {
    if (item.linkedAppointment) {
      const linkedAppointment = item.linkedAppointment;

      return `${item.doctor.name} (${convertDurationForFE(
        item.duration || 0
      )} mins) \n${linkedAppointment.doctor.name} (${convertDurationForFE(
        linkedAppointment.duration || 0
      )} mins)`;
    }

    if (item.isSplitScheduling && item.errorReason) {
      return `${item.doctor.name} (${convertDurationForFE(
        item.duration || 0
      )} mins) \nDoctor is unavailable`;
    }

    if (item.isSplitScheduling && !item.errorReason) {
      return `${item.doctor.name} (${convertDurationForFE(
        item.duration || 0
      )} mins) \nDoctor's working day`;
    }

    return item.doctor.name;
  };

  const getCurrentStatusForAppt = ({
    status,
    isAccepted,
    isCancelled,
  }: {
    status: string;
    isAccepted: boolean;
    isCancelled: boolean;
  }) => {
    const isNewStatus =
      status === APPOINTMENT_STATUS.NEW.KEY && isAccepted === false;

    const isCancelledStatus =
      (status === APPOINTMENT_STATUS.CANCELLED.KEY && isAccepted === false) ||
      isCancelled === true;

    const isAcceptedStatus =
      (status === APPOINTMENT_STATUS.ACCEPTED.KEY && isCancelled === false) ||
      (isAccepted === true && isCancelled === false);

    if (isNewStatus) {
      return APPOINTMENT_STATUS.NEW.KEY;
    } else if (isCancelledStatus) {
      return APPOINTMENT_STATUS.CANCELLED.KEY;
    } else if (isAcceptedStatus) {
      return APPOINTMENT_STATUS.ACCEPTED.KEY;
    }
  };

  const setStyleForCell = (
    cell: ExcelJS.Cell,
    {
      bgColor, // in argb
      textColor,
      isTextBold,
      isTextItalic,
      textAlignment,
      textSize,
    }: {
      bgColor?: string;
      textColor?: string;
      isTextBold?: boolean;
      isTextItalic?: boolean;
      textAlignment?: { vertical: any; horizontal: any };
      textSize?: number;
    }
  ) => {
    if (bgColor) {
      cell.style = {
        font: {
          italic: !!isTextItalic,
          bold: !!isTextBold,
          color: { argb: textColor || 'FF000000' },
          ...(textSize ? { size: textSize } : { size: 12 }),
        },
      };

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: bgColor },
      };

      cell.border = {
        top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
        right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
      };
    } else {
      cell.style = {
        font: {
          italic: !!isTextItalic,
          bold: !!isTextBold,
          color: { argb: textColor || 'FF000000' },
          ...(textSize ? { size: textSize } : { size: 12 }),
        },
      };
    }

    if (textAlignment) {
      cell.alignment = textAlignment;
    }

    return cell;
  };

  const applyDefaultFont = (worksheet: ExcelJS.Worksheet, fontOptions: any) => {
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = {
          ...cell.font,
          ...fontOptions,
        };
      });
    });
  };

  const applyDefaultWidth = (
    worksheet: ExcelJS.Worksheet,
    width: any,
    ignoredColumns: number[]
  ) => {
    for (let i = 1; i < 100; i++) {
      if (ignoredColumns.includes(i)) {
        worksheet.getColumn(i).width = 20;
        continue;
      }

      worksheet.getColumn(i).width = width;
    }
  };

  // Appointment tabs
  const calculateAndSetColumnWidthForAppointmentTab = (
    data: NormalizedDataType[],
    workSheet: ExcelJS.Worksheet
  ) => {
    for (let i = 0; i < data.length; i++) {
      const item = data[i] as any;

      HEADERS.forEach((header, index) => {
        const { key } = header;
        let length = item[key].length;

        if (key === 'practitionerName') {
          length = Math.ceil(item[key].length / 2) + EXTRA_WIDTH;
        } else if (['isBookedAfterClinicHour', 'isNewPatient'].includes(key)) {
          length = 2 * EXTRA_WIDTH;
        } else {
          length = item[key].length + EXTRA_WIDTH;
        }

        columnWidth.current[index] = Math.max(
          columnWidth.current[index],
          length
        );
      });
    }

    for (let i = 0; i < columnWidth.current.length; i++) {
      workSheet.getColumn(i + 1).width = columnWidth.current[i];
    }
  };

  const populateAppointmentData = (
    normalizedDataByClinic: NormalizedDataType[],
    worksheet: ExcelJS.Worksheet
  ) => {
    let lastPosition = 1;

    const appointmentList = normalizedDataByClinic.map((item3: any) => {
      const tmp: any = {};

      HEADERS.forEach((item: any) => {
        const key = item.label;

        tmp[key] = item3[item.key];
      });

      return tmp;
    });

    // For header
    const headerStartRow = lastPosition;

    let headerStartCol = lastPosition;

    HEADERS.forEach((item) => {
      const headerCell = worksheet.getCell(headerStartRow, headerStartCol);
      headerCell.value = item.label;
      setStyleForCell(headerCell, {
        isTextBold: true,
      });

      headerStartCol += 1;
    });

    // For body
    const bodyStartRow = lastPosition + 1;
    const bodyStartCol = lastPosition;

    appointmentList.forEach((item: any, index: number) => {
      const currentRow = bodyStartRow + index;

      HEADERS.forEach(({ label }, index) => {
        if (['After Hours', 'New Patient'].includes(label)) {
          worksheet.getCell(currentRow, bodyStartCol + index).value =
            item[label] === true ? 'Y' : '';
        } else {
          worksheet.getCell(currentRow, bodyStartCol + index).value =
            item[label];
        }
      });
    });

    calculateAndSetColumnWidthForAppointmentTab(
      normalizedDataByClinic,
      worksheet
    );

    // Reset column width for a new sheet
    columnWidth.current = Array.from({ length: HEADERS.length }).fill(
      defaultWidth
    );
  };

  const exportAppointmentDataForEachClinic = (
    normalizedData: NormalizedDataType[]
  ) => {
    const groupByClinic = groupBy(normalizedData, 'clinicId');
    const clinicIds = Object.keys(groupByClinic);

    for (let i = 0; i < clinicIds.length; i++) {
      const clinicId = clinicIds[i];

      const normalizedDataByClinic = normalizedData.filter(
        (item: NormalizedDataType) => item.clinicId === clinicId
      );

      const clinicName = normalizedDataByClinic[0]?.clinicName || 'Data';

      const cleanClinicName = clinicName
        .split('')
        .map((char) => {
          if (SPECIAL_CHARACTER.includes(char)) {
            return '';
          }
          return char;
        })
        .join('');

      const worksheet = workBook.current.addWorksheet(cleanClinicName);

      populateAppointmentData(normalizedDataByClinic, worksheet);

      applyDefaultFont(worksheet, { size: 12 });
    }
  };

  // Summary tab
  const populateSummaryData = ({
    section,
    worksheet,
  }: {
    section: Map<string, SummaryDataType[]>;
    worksheet: ExcelJS.Worksheet;
  }) => {
    let maxHeight = 1;

    const populateDataForEachStatus = ({
      fromRowPos,
      fromColPos,
      data,
      worksheet,
      status,
    }: {
      fromRowPos: number;
      fromColPos: number;
      data: any;
      worksheet: ExcelJS.Worksheet;
      status: string;
    }) => {
      let tmpFromRowPos = fromRowPos;
      let tmpFromColPos = fromColPos;

      let statusCell = worksheet.getCell(tmpFromRowPos, tmpFromColPos + 1);
      if (status === ALL_STATUS) {
        statusCell.value = 'Appointments Requested\n(All)';
      } else if (status === APPOINTMENT_STATUS.NEW.KEY) {
        statusCell.value = 'Appointments Requested Outstanding\n(Pending)';
      } else if (status === APPOINTMENT_STATUS.ACCEPTED.KEY) {
        statusCell.value = 'Appointments Accepted';
      } else if (status === APPOINTMENT_STATUS.CANCELLED.KEY) {
        statusCell.value = 'Rejected Appts';
      }

      statusCell = setStyleForCell(statusCell, {
        isTextBold: true,
        isTextItalic: true,
        textAlignment: {
          horizontal: 'right',
          vertical: 'middle',
        },
      });

      data.forEach(({ serviceName, total }: any, index: number) => {
        tmpFromRowPos += 1;

        const serviceCelPos = {
          value: serviceName,
          r: tmpFromRowPos,
          c: tmpFromColPos,
        };

        let serviceCell = worksheet.getCell(serviceCelPos.r, serviceCelPos.c);

        serviceCell.value = isNil(serviceCelPos.value)
          ? ''
          : serviceCelPos.value;

        const totalCelPos = {
          value: total,
          r: tmpFromRowPos,
          c: tmpFromColPos + 1,
        };

        let totalCell = worksheet.getCell(totalCelPos.r, totalCelPos.c);

        totalCell.value = isNil(totalCelPos.value) ? '' : totalCelPos.value;

        //--Set style--
        if (
          [
            'Appts Grand Total',
            'Total New Patients',
            'Total After Hours Appointments',
            'Appts Total',
            'New Patients',
            'After Hours Appointments',
          ].includes(serviceCelPos.value)
        ) {
          serviceCell = setStyleForCell(serviceCell, {
            isTextBold: true,
            bgColor: 'FFD8DEE8',
          });

          if (
            serviceCelPos.value === 'Appts Total' ||
            serviceCelPos.value === 'Appts Grand Total'
          ) {
            totalCell = setStyleForCell(totalCell, {
              bgColor: 'FF657BA3',
              textColor: 'FFFFFFFF',
              isTextBold: true,
            });
          } else {
            totalCell = setStyleForCell(totalCell, {
              isTextBold: true,
              bgColor: 'FFD8DEE8',
            });
          }
        } else {
          serviceCell = setStyleForCell(serviceCell, {
            bgColor: 'FFF2F4F7',
          });
        }
      });
    };

    section.forEach((value: SummaryDataType[], key: string) => {
      let rowIndex = maxHeight;
      let colIndex = 1;
      let height = {
        maxRowPerClinic: 0,
        maxServicePerClinic: 0,
      };

      //--Add title--
      let titleCell = worksheet.getCell(rowIndex, colIndex);
      titleCell.value = key;
      titleCell = setStyleForCell(titleCell, {
        isTextBold: true,
        textSize: 18,
      });

      //--Add body--
      [
        ALL_STATUS,
        APPOINTMENT_STATUS.NEW.KEY,
        APPOINTMENT_STATUS.ACCEPTED.KEY,
        APPOINTMENT_STATUS.CANCELLED.KEY,
      ].forEach((status, index) => {
        const infoByStatus =
          value.find((item: any) => {
            return item.currentStatus === status;
          }) || null;

        const data = infoByStatus?.data || [];

        populateDataForEachStatus({
          fromRowPos: rowIndex + 1,
          fromColPos: colIndex + index * 3,
          data,
          worksheet,
          status,
        });

        height.maxRowPerClinic = Math.max(
          height.maxRowPerClinic,
          data.length + 2
        );

        height.maxServicePerClinic = Math.max(
          height.maxServicePerClinic,
          data.length
        );
      });

      //--Calculate next cursor--
      const AMOUNT_OF_BREAK_LINES = 2;
      maxHeight += height.maxRowPerClinic + AMOUNT_OF_BREAK_LINES;

      //--Group Row--
      const fromGroupIndex = rowIndex + 2;
      for (
        let i = fromGroupIndex;
        i < fromGroupIndex + height.maxServicePerClinic - 4;
        i++
      ) {
        // the last 4 rows including "Appts grand total", "empty line", "total new patient" and "total after hours appts"
        worksheet.getRow(i).outlineLevel = 1;
      }
    });
  };

  const exportSummaryDataForAllClinics = (
    normalizedData: NormalizedDataType[]
  ) => {
    const worksheet = workBook.current.addWorksheet('Summary');

    // Set status
    let tmpNormalizedData = normalizedData.map((item) => {
      return {
        ...item,
        currentStatus: getCurrentStatusForAppt({
          status: item.status,
          isAccepted: item.isAccepted,
          isCancelled: item.isCancelled,
        }),
      };
    });

    const groupByClinicName = groupBy(tmpNormalizedData, 'clinicName');

    const clinicNames = Object.keys(groupByClinicName);

    const prepareSummaryDataForEachClinic = (
      normalizedData: NormalizedDataType[]
    ): SummaryDataType[] => {
      let maxServiceAmount = 0;

      const categorizeApptsByClinic = [
        ALL_STATUS,
        APPOINTMENT_STATUS.NEW.KEY,
        APPOINTMENT_STATUS.ACCEPTED.KEY,
        APPOINTMENT_STATUS.CANCELLED.KEY,
      ].map((status) => {
        const apptsByStatus = normalizedData.filter((item) =>
          status === ALL_STATUS ? true : item.currentStatus === status
        );

        let grandTotalAppts = 0;

        let totalApptsByService: {
          serviceName: string | null;
          total: number | null;
        }[] = [];

        let totalNewPatients = 0;

        let totalAfterHoursAppts = 0;

        const groupByServices = groupBy(apptsByStatus, 'serviceName');
        const services = orderBy(Object.keys(groupByServices));

        services.forEach((serviceName) => {
          const allApptsByService = groupByServices[serviceName];

          totalApptsByService.push({
            serviceName,
            total: allApptsByService.length,
          });

          grandTotalAppts += allApptsByService.length;

          totalNewPatients += allApptsByService.filter(
            (item) => item.isNewPatient
          ).length;

          totalAfterHoursAppts += allApptsByService.filter(
            (item) => item.isBookedAfterClinicHour
          ).length;
        });

        maxServiceAmount = Math.max(maxServiceAmount, services.length);

        return {
          currentStatus: status,
          totalApptsByService,
          grandTotalAppts,
          totalNewPatients,
          totalAfterHoursAppts,
          data: totalApptsByService,
        };
      });

      //--Fill empty line--
      const result = categorizeApptsByClinic.map((item) => {
        const { data } = item;

        const amountMissingFields = maxServiceAmount - data.length;

        const fillEmptyLineData = data.concat(
          Array(amountMissingFields).fill({
            serviceName: null,
            total: null,
          })
        );

        fillEmptyLineData.push(
          {
            serviceName:
              item.currentStatus === ALL_STATUS
                ? 'Appts Grand Total'
                : 'Appts Total',
            total: item.grandTotalAppts,
          },
          {
            serviceName: null,
            total: null,
          },
          {
            serviceName:
              item.currentStatus === ALL_STATUS
                ? 'Total New Patients'
                : 'New Patients',
            total: item.totalNewPatients,
          },
          {
            serviceName:
              item.currentStatus === ALL_STATUS
                ? 'Total After Hours Appointments'
                : 'After Hours Appointments',
            total: item.totalAfterHoursAppts,
          }
        );

        return {
          ...item,
          data: fillEmptyLineData,
        };
      });

      return result;
    };

    const section = new Map<string, SummaryDataType[]>();

    //--For all clinics--
    const dataCombination = prepareSummaryDataForEachClinic(tmpNormalizedData);
    const groupKeyName = `Group summary from ${moment(
      dateRange.fromDate,
      'YYYY-MM-DD'
    ).format('MMM DD yyyy')} to ${moment(dateRange.toDate, 'YYYY-MM-DD').format(
      'MMM DD yyyy'
    )}`;

    section.set(groupKeyName, dataCombination);

    //--For each clinic--
    for (let i = 0; i < clinicNames.length; i++) {
      const clinicName = clinicNames[i];

      const normalizedDataByClinic = groupByClinicName[clinicName];

      const result = prepareSummaryDataForEachClinic(normalizedDataByClinic);

      section.set(clinicName, result);
    }

    populateSummaryData({
      section,
      worksheet,
    });

    //--Set default width--
    applyDefaultWidth(worksheet, 40, [3, 6, 9]);
  };

  const getAppointment = async () => {
    setIsLoading(true);

    try {
      const query = new URLSearchParams({
        startDate: moment(dateRange.fromDate, 'YYYY-MM-DD')
          .startOf('days')
          .utc(true)
          .format(),
        endDate: moment(dateRange.toDate, 'YYYY-MM-DD')
          .endOf('days')
          .utc(true)
          .format(),
        sort,
        sortBy,
        ...(appointmentStatus !== 'all' && { status: appointmentStatus }),
        pmsType: PMS_TYPE.FIRST_IN,
      });

      selectedClinicIds.forEach((value) => {
        query.append('clinicIds[]', value);
      });

      const url = `/appointments?${query.toString()}`;
      const { data: axiosAppointmentResponse } = await axiosInstance.get(url, {
        headers: {
          Authorization: `JWT ${loadAuthToken()?.accessToken}`,
          ...(selectedClinicIds.length === 1
            ? { 'X-ClinicId': selectedClinicIds[0] }
            : {}),
        },
      });

      const normalizedData = axiosAppointmentResponse.data.map(
        (item: IAppointmentDetail) => {
          const appointmentDate = parseDateStringWithoutAffectTz(
            `${item.appointmentDate} ${item.startTime}`,
            `${DATE_TIME_FORMAT.APPOINTMENT_DATE} ${DATE_TIME_FORMAT.HH_MM_SS}`,
            item.timezone
          ).format('dddd, MMMM DD, YYYY hh:mm A (Z UTC)');

          const createdAtByClinicTimezone = moment(item.createdAt).tz(
            item.timezone
          );

          const requestTime = createdAtByClinicTimezone
            .clone()
            .seconds(0)
            .format('hh:mm:ss A');

          const requestDate = createdAtByClinicTimezone
            .clone()
            .format('YYYY-MM-DD');

          const requestDay = createdAtByClinicTimezone.clone().format('dddd');

          return {
            appointmentDate,
            clinicId: item.clinic!.id,
            clinicName: item.clinic!.name,
            patientFirstName: item.patient?.firstName || '',
            patientLastName: item.patient?.lastName || '',
            practitionerName: normalizePractitionerName(item),
            serviceName: item.service?.name || '',
            createdAt: moment(item.createdAt).format('MMM DD, YYYY hh:mm A'),
            requestDate,
            requestTime,
            requestDay,
            errorType: item.errorReason || '',
            status: item.status,
            isAccepted: item.isAccepted,
            isCancelled: item.isCancelled,
            isNewPatient: !item.isBookedByExistingPatient,
            isBookedAfterClinicHour:
              item.isBookedAfterClinicHour === null
                ? false
                : item.isBookedAfterClinicHour,
            appointmentStatus: getCurrentStatusForAppt({
              status: item.status,
              isAccepted: item.isAccepted,
              isCancelled: item.isCancelled,
            }),
            patientPhoneNumber: item.patient?.phoneNumber
              ? formatFullPhoneNumber(`+${item.patient.phoneNumber}`)
              : '',
            patientEmail: item.patient?.email || '',
            source:
              TRAFFIC_SOURCES.find(({ key }) => key === item.source)?.label ||
              '',
          };
        }
      );

      // Create "Summary" tab
      exportSummaryDataForAllClinics(normalizedData);

      // Create "Appointment" tab for each clinic
      exportAppointmentDataForEachClinic(normalizedData);

      const buffer = await workBook.current.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const fileName = `appointments-${moment(
        dateRange.fromDate,
        'YYYY-MM-DD'
      ).format('MMM DD yyyy')}-${moment(dateRange.toDate, 'YYYY-MM-DD').format(
        'MMM DD yyyy'
      )}.xlsx`;

      saveAs(blob, fileName);
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'An error has occurred. Please try again.',
      });
    } finally {
      workBook.current = new ExcelJS.Workbook();
    }

    setIsLoading(false);
  };

  return (
    <>
      <CommonButton
        variant="secondary"
        className="!min-h-[35px] h-3.5"
        isLoading={isLoading}
        disabled={isDataLoading}
        onClick={getAppointment}
      >
        <DownloadIcon
          className={isDataLoading ? 'all-child:all-child:fill-current' : ''}
        />
        <span className="ml-1">Export</span>
      </CommonButton>
    </>
  );
};

export default ExcelExport;
